<template>
  <div class="kye-date-range__wrapper">
    <!--<div class="kye-date-range__icon">
      <i class="el-input__icon el-icon-date"></i>
    </div>-->
    <div class="kye-date-range__box"
         :style="{width: `${dataPickerWidth * 2 + 17}px`}">
      <div class="kye-date-range__start"
           :style="{width: `${dataPickerWidth}px`}">
        <kye-date-picker prefix-icon="kye-date-range__prefix-icon"
                         ref="start"
                         style="width:100%;"
                         :append-to-body="appendToBody"
                         :readonly="readonly"
                         :type="currentType"
                         :format2="currentFormat"
                         :value-format="valueFormat"
                         v-model="currentDate[0]"
                         :placeholder="startPlaceholder"
                         :disabled="leftDisabled || disabled"
                         :picker-options="leftPickerOptions"
                         :clearable="false"
                         :showLunar="showLunar"
                         :defaultTime="defaultRangeTime[0]"
                         :operation="$attrs.operation"
                         popper-class="date-range__popper date-range__popper-start"
                         @input="v => handleChange(v, 0)"
                         @blur="handleBlur('start')"
                         @focus="handleFocus('start')">
        </kye-date-picker>
      </div>
      <div class="kye-date-range__split">
        <span>-</span>
      </div>
      <div class="kye-date-range__end"
           :style="{width: `${dataPickerWidth}px`}">
        <kye-date-picker prefix-icon="kye-date-range__prefix-icon"
                         ref="end"
                         :append-to-body="appendToBody"
                         style="width:100%;"
                         :readonly="readonly"
                         :type="currentType"
                         :format2="currentFormat"
                         :value-format="valueFormat"
                         v-model="currentDate[1]"
                         :placeholder="endPlaceholder"
                         :disabled="rightDisabled || disabled"
                         :picker-options="rightPickerOptions"
                         :clearable="false"
                         :showLunar="showLunar"
                         :defaultTime="defaultRangeTime[1]"
                         :operation="$attrs.operation"
                         popper-class="date-range__popper date-range__popper-end"
                         @input="v => handleChange(v, 1)"
                         @blur="handleBlur('end')"
                         @focus="handleFocus('end')">
        </kye-date-picker>
      </div>
    </div>
  </div>
</template>

<script>
  import { FORMAT, VALUE_FORMAT, DISABLED_DATE } from './index'
  import moment from 'moment'
  import { throttle } from 'lodash'

  const RANGE_WIDTH = {
    'yyyy-MM': 50,
    'yyyy-MM-dd HH:mm:ss': 118,
    daterange: 68,
    datetimerange: 102
  }

  export default {
    name: 'kye-date-range',
    props: {
      type: {
        type: String,
        default: 'daterange'
      },
      value: [Array, String, Number, Object],
      format: String,
      clearable: {
        type: Boolean,
        default: true
      },
      readonly: {
        type: Boolean,
        default: false
      },
      valueFormat: {
        type: String,
        default: 'yyyy-MM-dd HH:mm:ss'
      },
      startPlaceholder: String,
      endPlaceholder: String,
      disabled: {
        type: Boolean,
        default: false
      },
      leftDisabled: { // 左侧时间选择禁用
        type: Boolean,
        default: false
      },
      rightDisabled: { // 右侧时间选择禁用
        type: Boolean,
        default: false
      },
      rangePickerOptions: { // 区间禁用，和element功能保持一致，禁用时需注意如果选择左侧快捷区间，会影响时间区间取值
        type: Object,
        default: () => ({})
      },
      shortcutsCallback: { // 左侧面板回调，需返回一个数组
        type: Function,
        default: () => (null)
      },
      clearBoth: { // 清除currentData
        type: Boolean,
        default: false
      },
      singleValue: { // 保留单边的值，不清空整个currentData
        type: Boolean,
        default: false
      },
      showYearPanel: { // 是否显示选择年面板
        type: Boolean,
        default: false
      },
      appendToBody: { // 是否将弹窗append到body
        type: Boolean,
        default: true
      },
      showLunar: { // 是否显示农历
        type: Boolean,
        default: true
      },
      defaultTime: { // 默认时间
        type: Array,
        default: () => ([])
      },
    },
    data () {
      return {
        defaultRangeTime: Array.isArray(this.defaultTime) ? this.defaultTime : [],
        cacheDate: this.genCurrentDate(this.value),
        currentDate: this.genCurrentDate(this.value),
        showMonthSelect: false,
        showYearSelect: false,
        startPickerOptions: {
          disabledDate: this.createStartDisabledDate,
          shortcuts: this.createMonthSelectShortcuts()
        },
        endPickerOptions: {
          disabledDate: this.createEndDisabledDate,
          shortcuts: this.createMonthSelectShortcuts()
        },
        startYearPickerOptions: {
          disabledDate: this.createDisabledDateForYear,
          shortcuts: this.createShortcuts('Start')
        },
        endYearPickerOptions: {
          disabledDate: this.createDisabledDateForYear,
          shortcuts: this.createShortcuts('End')
        }
      }
    },
    created () {
      // 2021-08-25 处理兼容问题，pickerOptions正常情况下取不到值
      let options = this.$attrs.pickerOptions || this.$attrs['pickerOptions'] || this.rangePickerOptions
      this.disabledDate = options && options.disabledDate
    },
    watch: {
      value: {
        handler (val) {
          if (Array.isArray(val) && val[0] && val[1]) {
            this.currentDate = this.genCurrentDate(val)
            this.cacheDate = JSON.parse(JSON.stringify(this.currentDate))
          } else if (!val) {
            this.currentDate = ['', '']
          }
        }
      }
    },
    computed: {
      currentType () {
        return this.currentFormat === 'yyyy-MM' ? 'month' : this.type.replace('range', '')
      },
      dataPickerWidth () {
        return RANGE_WIDTH[this.format] || RANGE_WIDTH[this.type] || 50
      },
      currentFormat () {
        if (this.format) {
          return this.format
        } else {
          return this.type === 'daterange' ? 'yyyy-MM-dd' : this.type === 'datetimerange' ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM'
        }
      },
      leftPickerOptions () {
        if (this.showYearSelect) {
          return this.startYearPickerOptions
        }
        return this.showMonthSelect ? this.startPickerOptions : this.createPickerOptions('Start')
      },
      rightPickerOptions () {
        if (this.showYearSelect) {
          return this.endYearPickerOptions
        }
        return this.showMonthSelect ? this.endPickerOptions : this.createPickerOptions('End')
      }
    },
    methods: {
      createMonthSelectShortcuts () {
        let months = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
        return [
          {
            text: '返回',
            onClick: picker => {
              this.showMonthSelect = false
              this.monthSelected = null
              if (picker.$children.length) {
                let table = picker.$children.find(com => com.$vnode.componentOptions.tag === 'date-table')
                table.$el.querySelectorAll('td').forEach(el => {
                  el.classList.remove('kye-start', 'kye-end')
                })
              }
            }
          },
          ...months.map((month, index) => ({
            text: month,
            onClick: picker => {
              this.monthSelected = null
              this.startPickerOptions.disabledDate = this.createDisabledDateForMonth.bind(null, index)
              this.endPickerOptions.disabledDate = this.createDisabledDateForMonth.bind(null, index)
              this.monthSelectShortcutsClick(picker, index)
            }
          }))
        ]
      },
      monthSelectShortcutsClick (picker, month) {
        let isStart = picker.$el.className.includes('date-range__popper-start')
        // 快捷方式 选择月份 根据下拉面板 切换的年份  picker.date ：下拉面板实例 获取下拉面板当前时间
        let start = FORMAT(moment(picker.date).month(month).startOf('month').startOf('day'), VALUE_FORMAT)
        let end = FORMAT(moment(picker.date).month(month).endOf('month').endOf('day'), VALUE_FORMAT)
        this.currentDate = [start, end]
        if (isStart && this.cacheDate[0] === start) {
          this.handleChange(end, 1)
        }
        if (!isStart && this.cacheDate[1] === end) {
          this.handleChange(start, 0)
        }
        picker.$emit('pick', isStart ? start : end, true)
        this.setStartAndEndStyle(picker, {start, end})
        this.monthSelected = true
      },
      createPickerOptions (v) {
        let c = { disabledDate: this[`create${v}DisabledDate`] }
        let shortCuts = this.createShortcuts(v)
        if (shortCuts && shortCuts.length) {
          c['shortcuts'] = shortCuts
        }
        return c
      },
      createStartDisabledDate (time) {
        if (this.currentDate[1]) {
          let f = time.getTime() > moment(this.currentDate[1]).endOf('day').unix() * 1000
          return this.disabledDate ? this.disabledDate(time) || f : f
        } else if (this.disabledDate) {
          return this.disabledDate(time)
        } else {
          return time.getTime() > DISABLED_DATE
        }
      },
      createEndDisabledDate (time) {
        if (this.currentDate[0]) {
          const isBefore = time.getTime() < moment(this.currentDate[0]).startOf('day').unix() * 1000
          const defaultDisabled = isBefore || time.getTime() > DISABLED_DATE
          return this.disabledDate ? (this.disabledDate(time) || isBefore) : defaultDisabled
        } else if (this.disabledDate) {
          return this.disabledDate(time)
        } else {
          return time.getTime() > DISABLED_DATE
        }
      },
      createDisabledDateForMonth (index, time) {
        let s = time.getTime() < moment().month(index).startOf('month').startOf('day').unix() * 1000 || time.getTime() > DISABLED_DATE
        // let e = time.getTime() > moment().month(index).endOf('month').endOf('day').unix() * 1000
        return s
      },
      createDisabledDateForYear (time) {
        const s = time.getTime() < moment().year(time).startOf('month').startOf('day').unix() * 1000 || time.getTime() > DISABLED_DATE
        return s
      },
      createShortcuts (v) {
        let shortcuts = []
        if (this.clearable) {
          shortcuts = [...shortcuts, {
            text: '清除',
            onClick: (picker) => this.handleClearOperation(picker, v)
          }]
        }
        if (this.currentFormat === 'yyyy-MM') {
          shortcuts = [...shortcuts, {
            text: '本月',
            // onClick (picker) {
            //   picker.$emit('pick', FORMAT(new Date(), 'yyyy-MM'))
            // }
            onClick: this.handleMonthPick
          }, {
            text: '上月',
            // onClick (picker) {
            //   picker.$emit('pick', FORMAT(moment().subtract(1, 'month'), 'yyyy-MM'))
            // }
            onClick: this.handleLastMonthPick
          }]
        } else {
          shortcuts = [
            ...shortcuts,
            {
              text: '今天',
              onClick: this.handleTodayPick
            },
            {
              text: '昨天',
              onClick: this.handleYesterdayPick
            },
            {
              text: '一周',
              onClick: this.handleWeekPick
            },
            {
              text: '当月',
              onClick: this.handleMonthPick
            },
            {
              text: '近30天',
              onClick: this.handleOneMonthPick
            },
            {
              text: '上月',
              onClick: this.handleLastMonthPick
            },
            {
              text: '选择月',
              onClick: picker => {
                this.showMonthSelect = true
                this.showYearPanel && this.setYearPanel(picker, false)
              }
            }
          ]
        }
        if (this.showYearPanel) {
          shortcuts.push({
            text: '选择年',
            onClick: picker => {
              this.setYearPanel(picker)
            }
          })
        }
        // 2021-08-25 增加回调，可修改左侧面板
        return this.shortcutsCallback(shortcuts, this.callbackClickPicker) || shortcuts
      },
      handleTodayPick (picker) {
        this.onClickPicker(picker, 'today')
      },
      handleYesterdayPick (picker) {
        this.onClickPicker(picker, 'yesterday')
      },
      handleWeekPick (picker) {
        this.onClickPicker(picker, 'week')
      },
      handleMonthPick (picker) {
        this.onClickPicker(picker, 'month')
      },
      handleOneMonthPick (picker) {
        this.onClickPicker(picker, 'oneMonth')
      },
      handleLastMonthPick (picker) {
        this.onClickPicker(picker, 'lastMonth')
      },
      // 2021-08-25 增加回调，控制左侧面板显示，赋值
      callbackClickPicker (picker, userArr) {
        let isStart = picker.$el.className.includes('date-range__popper-start')

        let start = FORMAT(moment(userArr[0]).startOf('day'), VALUE_FORMAT)
        let end = FORMAT(moment(userArr[1]).endOf('day'), VALUE_FORMAT)
        this.currentDate = [start, end]
        if (isStart && this.cacheDate[0] === start) {
          this.handleChange(end, 1)
        }
        if (!isStart && this.cacheDate[1] === end) {
          this.handleChange(start, 0)
        }

        picker.$emit('pick', isStart ? start : end)
      },
      onClickPicker (picker, t) {
        let isStart = picker.$el.className.includes('date-range__popper-start')

        let s = new Date()
        let e = new Date()
        switch (t) {
          case 'yesterday':
            s = moment().subtract(1, 'days')
            e = moment().subtract(1, 'days')
            break
          case 'week':
            s = moment().subtract(6, 'days')
            break
          case 'month':
            s = FORMAT(s, 'yyyy-MM')
            break
          case 'oneMonth':
            s = moment().subtract(30, 'day')
            break
          case 'lastMonth':
            s = moment().subtract(1, 'month').startOf('month')
            e = moment().subtract(1, 'month').endOf('month')
            break
        }

        let start = FORMAT(moment(s).startOf('day'), VALUE_FORMAT)
        let end = FORMAT(moment(e).endOf('day'), VALUE_FORMAT)
        this.currentDate = [start, end]
        if (isStart && this.cacheDate[0] === start) {
          this.handleChange(end, 1)
        }
        if (!isStart && this.cacheDate[1] === end) {
          this.handleChange(start, 0)
        }
        picker.$emit('pick', isStart ? start : end)
      },
      genCurrentDate (d) {
        let arr = []
        if (d) {
          if (typeof d === 'string' || typeof d === 'number') {
            arr = [d, ''].map(v => FORMAT(v, this.valueFormat))
          } else if (d.length === 0) {
            arr = ['', '']
          } else {
            arr = d.map(v => FORMAT(v, this.valueFormat))
          }
        } else {
          arr = ['', '']
        }
        return arr
      },
      handleChange (val, index) {
        let d = JSON.parse(JSON.stringify(this.currentDate))
        if (this.monthSelected) {
          this.currentDate[0] = this.cacheDate[0]
          d[0] = this.cacheDate[0]
          d[1] = val
          d = d.map(v => FORMAT(v, 'yyyy-MM-dd HH:mm:ss'))
          if (this.type === 'daterange') {
            // 2022-07-12 兜底判断，val可能不存在，FORMAT方法无法解析，导致日期范围组件出现死循环
            // 类似 moment('12:00:00').format('YYYY-MM-DD HH:mm:ss') 解析为 'Invalid date'
            d[0] = d[0] ? d[0].slice(0, 11) + '00:00:00' : ''
            d[1] = d[1] ? d[1].slice(0, 11) + '23:59:59' : ''
          } else if (this.type === 'datetimerange' && d[0] === d[1]) {
            d[1] = d[1].slice(0, 11) + '23:59:59'
          }
          if (new Date(d[0]).getTime() > new Date(d[1]).getTime()) {
            d[1] = this.cacheDate[1]
          }
          d = d.map(v => FORMAT(v, this.valueFormat))
        if (!this.clearable && d.length === 0 && this.cacheDate[0] && this.cacheDate[1]) {
          d = JSON.parse(JSON.stringify(this.cacheDate))
        }
        this.$emit('input', d)
        let ref = index === 0 ? 'start' : 'end'
        this.setStartAndEndStyle(this.$refs[ref].$refs[ref].picker, {start: d[0], end: d[1]})
        } else {
          if (this.showYearSelect) {
            let ref = index === 0 ? 'start' : 'end'
            const year = val.substr(0, 4)
            d[0] = `${year}-01-01 00:00:00`
            d[1] = `${year}-12-31 23:59:59`
            this.setYearPanel(this.$refs[ref].$refs[ref].picker, false)
          }
          if (d[0] && d[1]) {
            d = d.map(v => FORMAT(v, 'yyyy-MM-dd HH:mm:ss'))
            if (this.type === 'daterange') {
              d[0] = d[0].slice(0, 11) + '00:00:00'
              d[1] = d[1].slice(0, 11) + '23:59:59'
            } else if (this.type === 'datetimerange' && d[0] === d[1]) {
              d[1] = d[1].slice(0, 11) + '23:59:59'
            }
            if (new Date(d[0]).getTime() > new Date(d[1]).getTime()) {
              // 2022-08-08 年月日时分秒时，用户自定义时分，cacheDate 还是为['', '']
              // d[index] = this.cacheDate[index]
              const minDate = d[index === 0 ? 1 : 0]
              d[index] = this.cacheDate[index] ? this.cacheDate[index] : minDate
            }
            d = d.map(v => FORMAT(v, this.valueFormat))
          } else {
            if (!this.singleValue) {
              d = []
            } else {
              if (this.type === 'daterange') {
                d = d.map(v => FORMAT(v, 'yyyy-MM-dd HH:mm:ss'))
                d[0] = d[0] ? d[0].slice(0, 11) + '00:00:00' : ''
                d[1] = d[1] ? d[1].slice(0, 11) + '23:59:59' : ''
                d = d.map(v => FORMAT(v, this.valueFormat))
              }
            }
          }
          if (!this.clearable && d.length === 0 && this.cacheDate[0] && this.cacheDate[1]) {
            d = JSON.parse(JSON.stringify(this.cacheDate))
          }
          if (!d[0] && !d[1]) {
            d = []
          }
          this.$emit('input', d)
        }
      },
      setStartAndEndStyle (picker, {start, end}) {
        if (picker.$children.length) {
          let table = picker.$children.find(com => com.$vnode.componentOptions.tag === 'date-table')
          let _start = FORMAT(start, 'yyyy-MM-dd')
          let _end = FORMAT(end, 'yyyy-MM-dd')
          table.$nextTick(_ => {
            table.$el.querySelectorAll('td').forEach(el => {
              let _row = el.parentNode.rowIndex - 1
              let _column = el.cellIndex
              let _date = FORMAT(table.getDateOfCell(_row, _column), 'yyyy-MM-dd')
              if (_date === _start) {
                el.classList.add('kye-start')
              } else if (_date === _end) {
                el.classList.add('kye-end')
              } else {
                el.classList.remove('kye-start', 'kye-end')
              }
            })
          })
        }
      },
      setYearPanel (picker, show = true) {
        const type = this.currentType === 'month' ? 'month' : 'day'
        this.showYearSelect = show
        // selectionMode 为 day/month/week/year/dates
        picker.selectionMode = show ? 'year' : type
        // 日期面板显示方法
        picker.resetView()
      },
      addDateTableEvents (picker, isStart) {
        if (picker.$children.length) {
          let table = picker.$children.find(com => com.$vnode.componentOptions.tag === 'date-table')
          // 为date-table添加mousedown事件，并绑定参数
          table.$el.addEventListener('mousedown', this.handleMouseDown.bind(null, picker, table, isStart))
        }
      },
      removeDateTableEvents (picker) {
        if (picker.$children.length) {
          let table = picker.$children.find(com => com.$vnode.componentOptions.tag === 'date-table')
          table.$el.removeEventListener('mousedown', this.handleMouseDown)
        }
      },
      handleMouseDown (picker, table, isStart, e) {
        let position = this.getRowAndColumn(e)
        if (position) {
          const {row, column} = position
          table.lastRow = row
          table.lastColumn = column
          table.end = table.start = table.getDateOfCell(row, column)
          table.$el.querySelectorAll('td').forEach(el => {
            let _row = el.parentNode.rowIndex - 1
            let _column = el.cellIndex
            let _date = table.getDateOfCell(_row, _column)
            // 给第一个td添加样式，并清除其他td的样式
            if (+new Date(_date) === +new Date(table.start)) {
              el.classList.add('kye-range')
            } else {
              el.classList.remove('kye-range')
            }
          })
          const handleMouseMove = (e) => {
            let position = this.getRowAndColumn(e)
            if (position) {
              const {row, column} = position
              if (row !== table.lastRow || column !== table.lastColumn) {
                table.lastRow = row
                table.lastColumn = column
                table.end = table.getDateOfCell(row, column)
                table.$el.querySelectorAll('td').forEach(el => {
                  let _row = el.parentNode.rowIndex - 1
                  let _column = el.cellIndex
                  let _date = table.getDateOfCell(_row, _column)
                  // 正向拖动、反向拖动分开处理
                  if (table.start <= table.end) {
                    if (_date >= table.start && _date <= table.end) {
                      el.classList.add('kye-range')
                    } else {
                      el.classList.remove('kye-range')
                    }
                  } else {
                    if (_date <= table.start && _date >= table.end) {
                      el.classList.add('kye-range')
                    } else {
                      el.classList.remove('kye-range')
                    }
                  }
                })
              }
            }
          }
          const _handleMouseMove = throttle(handleMouseMove, 16)
          table.$el.addEventListener('mousemove', _handleMouseMove)

          const handleMouseUp = (e) => {
            let position = this.getRowAndColumn(e)
            if (position) {
              let start = table.start <= table.end ? table.start : table.end
              let end = table.start <= table.end ? table.end : table.start
              start = FORMAT(moment(start).startOf('day'), VALUE_FORMAT)
              end = FORMAT(moment(end).endOf('day'), VALUE_FORMAT)
              // 开始和结束时间相等不处理，交给原来的组件去处理
              if (+new Date(table.start) !== +new Date(table.end)) {
                this.currentDate = [start, end]
                // mouseup触犯input事件
                if (isStart && this.cacheDate[0] === start) {
                  this.handleChange(end, 1)
                }
                if (!isStart && this.cacheDate[1] === end) {
                  this.handleChange(start, 0)
                }
                // mouseup触犯pick事件，根据type判断是否收起面板
                picker.$emit('pick', isStart ? start : end, this.type === 'datetimerange')
                // 清除所有添加的样式
                table.$el.querySelectorAll('td').forEach(el => {
                  el.classList.remove('kye-range')
                })
              }
            }
            // table.$el.removeEventListener('mousedown', handleMouseDown)
            table.$el.removeEventListener('mousemove', _handleMouseMove)
            table.$el.removeEventListener('mouseup', handleMouseUp)
          }
          table.$el.addEventListener('mouseup', handleMouseUp)
        }
      },
      // 计算mousedown点击的元素是都满足条件，并返回坐标
      getRowAndColumn (e) {
        let target = e.target
        if (target.tagName === 'SPAN') {
          target = target.parentNode.parentNode
        }
        if (target.tagName === 'DIV') {
          target = target.parentNode
        }
        if (target.tagName !== 'TD') return
        // 只作用于当月时间范围
        if (target.className.includes('prev-month') || target.className.includes('next-month')) return

        const row = target.parentNode.rowIndex - 1
        const column = target.cellIndex
        return {row, column}
      },
      handleFocus (e) {
        this.$emit('focus')
        // 为date-table添加事件
        // if (this.type === 'daterange' || this.type === 'datetimerange') {
        //   this.$nextTick(_ => {
        //     let picker = this.$refs[e].$refs[e].picker
        //     this.addDateTableEvents(picker, e === 'start')
        //   })
        // }
      },
      handleBlur (e) {
        if (!this.clearable && this.cacheDate[0] && this.cacheDate[1]) {
          this.currentDate = JSON.parse(JSON.stringify(this.cacheDate))
        }
        this.$emit('blur')
        // 清除date-table的事件
        // if (this.type === 'daterange' || this.type === 'datetimerange') {
        //   let picker = this.$refs[e].$refs[e].picker
        //   this.removeDateTableEvents(picker)
        // }
      },
      focus () {
        this.$refs.start.focus()
      },
      // 清除的逻辑
      handleClearOperation (picker, v) {
        // 如果清除所有的值，则直接返回空
        if (this.clearBoth) {
          this.currentDate = []
          this.$emit('input', [])
        }
        picker.$emit('pick', null)
      }
    }
  }
</script>

<style lang="scss">
  @import '../../assets/scss/variable.scss';

  .kye-date-range {
    &__wrapper {
      height: 28px;
      border: 1px solid var(--g-border-color);
      background-color: #ffffff;
      border-radius: 4px;
      padding-left: 8px;
      .el-input__inner {
        border: 0;
      }
      &:hover {
        border-color: var(--g-color-primary-hover);
      }
      .el-input--mini .el-input__inner {
        height: 24px !important;
        line-height: 24px !important;
      }
      .el-date-editor.el-input--mini {
        top: -1px;
      }
      .el-input--mini,
      .el-input--mini .el-input__icon {
        height: 24px;
        line-height: 24px;
      }
      .el-date-editor--datetime.el-input--prefix .el-input__inner {
        padding-left: 0;
      }
      .el-date-editor--datetime.el-input--suffix .el-input__inner {
        padding-right: 0;
      }
      .el-input__suffix {
        right: 0;
      }
    }
    &__icon {
      display: inline-block;
      height: 26px;
      width: 20px;
      color: #c0c4cc;
      margin-right: -2px;
      .el-input__icon {
        line-height: 26px;
      }
    }
    &__box {
      display: inline-block;
      height: 26px;
      line-height: 26px;
    }
    &__start {
      display: inline-block;
      position: relative;
      height: 26px;
      line-height: 26px;
    }
    &__split {
      display: inline-block;
      width: 16px;
      height: 12px;
      margin-left: -8px;
      margin-right: 0;
      padding-left: 6px;
      text-align: center;
    }
    &__end {
      display: inline-block;
      position: relative;
      height: 26px;
      line-height: 26px;
    }
    .kye-date-range__prefix-icon {
      display: none;
    }
  }

  .date-range__popper {
    &.el-date-picker.has-sidebar {
      width: 398px;
    }
    .el-picker-panel__sidebar {
      width: 70px;
    }
    .el-picker-panel__sidebar + .el-picker-panel__body {
      margin-left: 70px;
    }
    .el-picker-panel__shortcut {
      line-height: 22px;
    }
    .el-date-table {
      .kye-range div {
        background-color: #fdf6ec;
      }
      .kye-start div span{
        color: #fff;
        background-color: #909399;
      }
      .kye-end div span{
        color: #fff;
        background-color: #ff9300;
      }
    }
  }

  // 增加权重，防止覆盖
  .ky-erp .kye-date-range {
    &__wrapper {
      .el-input--prefix .el-input__inner {
        padding-left: 0 !important;
      }
      .el-input--suffix .el-input__inner {
        padding-right: 0 !important;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/public/components/kye-date-picker/date-range.vue